/**
 * Global state for rewards
 *
 * <i>Copyright (c) 2020 Energi Cryptocurrency - https://energi.world</i><br>
 * Proprietary License
 *
 *
 * @module globals/rewards.js
 * @class Rewards
 * @since 0.7.12
 */

import Web3 from 'web3';
import energiExtension from '@energi/web3-ext';
import {createStore} from 'effector';
import networkInfo from '../lib/constants/network-info';
import localStorageInstance from '../lib/helpers/localstorage';
import blockPolling from './block-polling';
import masternodeListing from './masternode-listing';

/* ********************************************************************************************************** */
/* ********************************************************************************************************** */

const {$lastBlock} = blockPolling,
    {$stats} = masternodeListing;

let polling = false,
    web3;

const STAKING_K = 0.08, // Correction factor to calculate total amount of staked NRG out of the difficulty. The higher, the higher the calculated difficulty and the lower the staking rewards
    STAKING_J = 1; // Correction to assure the rewards are giving a too positive result when the difficulty fluctuates much. A higher the value will correct the staking rewards downwards

// TODO1: STAKING_K -> needs to be finetuned.
// TODO2: STAKING_J -> needs to be set to 1.5 once STAKING_K has the final setting.

const MINUTES_PER_DAY = 1440,
    MIN_PER_YEAR = 365 * MINUTES_PER_DAY,
    STAKING_REWARD = 2.28,
    MN_REWARD = 0.914,
    REWARDED_COLLATERAL_PER_BLOCK = 10000,
    MN_REWARDS_RATE_PER_THOUSAND_NRG = MN_REWARD / 1000,
    REWARD_CORRECTION = 0.9, // Management decided to show 10% less than the actual rewards
    toBN = Web3.utils.toBN,
    etherWeiBN = toBN(Web3.utils.unitMap.ether),
    BN0 = toBN('0');

/* ********************************************************************************************************** */
/* ********************************************************************************************************** */

const $store = createStore({
    staking: BN0,
    masternode: BN0,
    payoutCycleTime: '',
    totalAmountAtStake: []
});

const on = {
    startPolling: () => {
        const rewards = calculateState();
        polling = true;
        $store.setState(rewards);
    },
    stopPolling: () => {
        polling = false;
    }
};

web3 = new Web3(networkInfo.networkAddress);
energiExtension.extend(web3);

/* ********************************************************************************************************** */
/* ********************************************************************************************************** */

/**
* Calculates the reward state
*
* @method calculateState
* @protected
* @return {Object} The actual state
* @since 0.7.12
*/
const calculateState = () => {
    // new block
    // const remaining = await web3.eth.getBalance(networkInfo.smartContractAddress),
    const stats = getStats();
    if (Object.keys(stats).length === 0) {
        return $store.getState();
    }
    const activeCollateral = stats.activeCollateral.div(etherWeiBN);
    // make sure to set the time in UTC, to prevent hours changes
    let payoutCycleTimeMin, masternodeRewards, stakingRewards, totalAmountAtStake, averageTotalAmountAtStake;

    payoutCycleTimeMin = Math.round(activeCollateral.toString() / (REWARD_CORRECTION * REWARDED_COLLATERAL_PER_BLOCK));
    masternodeRewards = getAnnualMasternodeRewards(payoutCycleTimeMin);
    totalAmountAtStake = getAmountStaked();
    averageTotalAmountAtStake = totalAmountAtStake.length ? totalAmountAtStake.reduce((cumm, item) => cumm + parseInt(item.staked, 10), 0) / totalAmountAtStake.length : 0;
    stakingRewards = REWARD_CORRECTION * getAnnualStakingRewards(averageTotalAmountAtStake);

    // according to #3326 we set the stakingrewards to masterndoerewards -/- 9%
    stakingRewards = Math.max(1, masternodeRewards - 9);

    // according to Tommy, we need to show the rewards 10% below actual
    return {
        staking: stakingRewards,
        masternode: masternodeRewards,
        totalAmountAtStake,
        payoutCycleTime: payoutCycleTimeMin,
    };
};

/**
* Calculates the annual masternode reward in percentage
*
* @method getAnnualMasternodeRewards
* @param payoutCycleTime {Date}
* @protected
* @return {Number} annual masternode rewards in percentage
* @since 0.7.12
*/
const getAnnualMasternodeRewards = payoutCycleTimeMin => {
    return (MIN_PER_YEAR / payoutCycleTimeMin) * MN_REWARDS_RATE_PER_THOUSAND_NRG * 100; // percent
};

/**
* Gets the total amount of staked NRG based out of the difficulty
*
* @method getAmountStaked
* @param amountAtStakeNrg {Number} the amount of NRG that is staked
* @protected
* @return {Array} staking rewards in NRG per block
* @since 0.7.12
*/
const getAmountStaked = () => {
    const difficulties = localStorageInstance.getObject('blockdifficulties');
    // not stored in localstorage yet, return 0
    if (!difficulties || !difficulties.length) {
        return [];
    }
    return difficulties.map(d => {
        return {
            block: d.block,
            staked: STAKING_K * d.difficulty
        };
    });
};

/**
* Calculates the amount of staking rewards in NRG
*
* @method getAnnualStakingNrg
* @param amountAtStakeNrg {Number} the amount of NRG that is staked
* @param totalAmountAtStake {Number} total amount of NRG that is stakes
* @protected
* @return {Number} staking rewards in NRG on annual base
* @since 0.7.12
*/
const getAnnualStakingNrg = (amountAtStakeNrg, totalAmountAtStake) => {
    const blockRewardIntervalMin = STAKING_J / (amountAtStakeNrg / totalAmountAtStake);
    return STAKING_REWARD * (MIN_PER_YEAR / blockRewardIntervalMin);
};

/**
* Calculates the annual staking reward in percentage
*
* @method getAnnualStakingRewards
* @protected
* @return {Number} annual staking rewards in percentage
* @since 0.7.12
*/
const getAnnualStakingRewards = totalAmountAtStake => {
    const stakedNRG = 10000; // arbitrary number, but needed to calculate percentage gain
    return (getAnnualStakingNrg(stakedNRG, totalAmountAtStake) / stakedNRG) * 100; // percent
};

/**
* Gets masternode stats from the bloackchain.
*
* @method getStats
* @protected
* @return {Object} masternode stats
* @since 0.7.12
*/
const getStats = () => masternodeListing.$stats.getState();

/* ********************************************************************************************************** */
/* ********************************************************************************************************** */

// $store.on(on.startPolling, async () => {
//     const newState = await calculateState();
//     polling = true;
//     return newState;
// });

$lastBlock.watch(() => {
    let newState;
    // new block
    if (polling) {
        newState = calculateState();
        $store.setState(newState);
    }
});

$stats.watch(() => {
    if (polling) {
        const rewards = calculateState();
        $store.setState(rewards);
    }
});

/* ********************************************************************************************************** */
/* ********************************************************************************************************** */

/**
* @property rewards
*     @property {Object} rewards.$store all Masternode Owner data
*         @property {number} staking -> annual staking reward in percentage
*         @property {number} masternode -> annual masternode reward in percentage
*         @property {Date} payoutCycleTime -> Date; to be formatted in [uu:mm]
*     @property {Object} rewards.on
*         @property {Function} rewards.on.startPolling sets $store and initiates the polling process
*         @property {Function} rewards.on.stopPolling halts the polling process
* @since 0.4.0
*/
const rewards = {
    $store,
    on
};

export default rewards;
